import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import translate from "@insite/client-framework/Translate";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomDynamicFormState } from "@midland-industries/Store/Data/DynamicForms/DynamicFormReducer";
import AddFormToState from "@midland-industries/Store/Data/DynamicForms/Handlers/AddFormToState";
import beginSubmitDynamicForm from "@midland-industries/Store/Data/DynamicForms/Handlers/BeginSubmitDynamicForm";
import clearDynamicForm from "@midland-industries/Store/Data/DynamicForms/Handlers/ClearDynamicForm";
import endSubmitDynamicForm from "@midland-industries/Store/Data/DynamicForms/Handlers/EndSubmitDynamicForm";
import submitDynamicForm from "@midland-industries/Store/Data/DynamicForms/Handlers/SubmitDynamicForm";
import {
    DynamicFormButtonShapes,
    DynamicFormButtonTypes,
    DynamicFormButtonVariants,
    DynamicFormFieldModel,
    DynamicFormThirdPartyApiTypes,
} from "@midland-industries/Types/DynamicFormModels";
import { determineFormIndex } from "@midland-industries/Utilities/DetermineFormIndex";
import React, { FC, RefObject, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    formName = "formName",
    emailRecipients = "emailRecipients",
    submitButtonText = "submitButtonText",
    successMessage = "successMessage",
    buttonVariant = "buttonVariant",
    buttonShape = "buttonShape",
    buttonType = "buttonType",
    showResetButton = "showResetButton",
    enableRecaptcha = "enableRecaptcha",
    sendToThirdPartyApi = "sendToThirdPartyApi",
    sendToApiType = "sendToApiType",
    customApiEndpoint = "customApiEndpoint",
    endpointRequiresAuth = "endpointRequiresAuth",
    endpointUserId = "endpointUserId",
    endpointPassword = "endpointPassword",
    displayCustomApiResponse = "displayCustomApiResponse",
}

interface OwnProps {
    id: string;
    onSuccessfulSubmit?: () => void;
    skipModalOnSuccess?: boolean;
    fields: {
        [fields.formName]: string;
        [fields.emailRecipients]: string;
        [fields.submitButtonText]: string;
        [fields.successMessage]: string;
        [fields.buttonVariant]: DynamicFormButtonVariants;
        [fields.buttonShape]: DynamicFormButtonShapes;
        [fields.buttonType]: DynamicFormButtonTypes;
        [fields.showResetButton]: boolean;
        [fields.enableRecaptcha]: boolean;
        [fields.sendToThirdPartyApi]: boolean;
        [fields.sendToApiType]: DynamicFormThirdPartyApiTypes;
        [fields.customApiEndpoint]: string;
        [fields.endpointRequiresAuth]: boolean;
        [fields.endpointUserId]: string;
        [fields.endpointPassword]: string;
        [fields.displayCustomApiResponse]: boolean;
    };
}

const mapStateToProps = (state: CustomDynamicFormState) => {
    const dynamicFormData = state.xcentiumDynamicForms;
    return {
        dynamicFormData,
    };
};

const mapDispatchToProps = {
    AddFormToState,
    submitDynamicForm,
    clearDynamicForm,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
    beginSubmitDynamicForm,
    endSubmitDynamicForm,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface DynamicFormStyles {
    container?: GridContainerProps;
    formGridItem?: GridItemProps;
    spinner?: LoadingOverlayProps;
    buttonWrapper?: InjectableCss;
    submitButton?: ButtonPresentationProps;
    resetButton?: ButtonPresentationProps;
    successModal?: ModalPresentationProps;
    modalContainer?: GridContainerProps;
    messageTextGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    buttonGridItem?: GridItemProps;
    continueButton?: ButtonPresentationProps;
    customApiResultWrapper?: InjectableCss;
    customApiResultLineWrapper?: InjectableCss;
    customApiResultText?: TypographyPresentationProps;
}

export const dynamicFormStyles: DynamicFormStyles = {
    container: { gap: 0 },
    formGridItem: {
        width: 12,
        css: css`
            width: 100%;
        `,
    },
    spinner: {
        css: css`
            width: 100%;
        `,
    },
    resetButton: {
        css: css`
            float: right;
            margin-top: 20px;
            border: 2px solid #e64e25;
            color: #e64e25;
        `,
    },
    submitButton: {
        css: css`
            float: right;
            margin-top: 20px;
            margin-left: 20px;
        `,
    },
    successModal: { sizeVariant: "small" },
    messageTextGridItem: { width: 12 },
    buttonGridItem: {
        width: 12,
    },
    buttonWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: flex-end;
            align-items: center;
        `,
    },
    customApiResultWrapper: {
        css: css`
            width: 100%;
            margin-top: 30px;
            margin-bottom: 30px;
        `,
    },
    customApiResultLineWrapper: {
        css: css`
            width: 100%;
        `,
    },
};

const styles = dynamicFormStyles;

type DynamicFormValidator = {
    fieldKey: string;
    validate: () => { isValid: boolean; errorMessage: string };
};

type DynamicFormContextType = {
    formName: string;
    validators: DynamicFormValidator[];
};

export const DynamicFormContext = React.createContext<DynamicFormContextType>({
    formName: "",
    validators: [] as DynamicFormValidator[],
});

const DynamicForm: FC<Props> = props => {
    const { id, fields, dynamicFormData, children, skipModalOnSuccess } = props;
    const {
        AddFormToState,
        beginSubmitDynamicForm,
        submitDynamicForm,
        clearDynamicForm,
        validateReCaptcha,
        endSubmitDynamicForm,
        onSuccessfulSubmit,
    } = props;
    const {
        formName,
        emailRecipients,
        submitButtonText,
        successMessage,
        buttonVariant,
        buttonShape,
        buttonType,
        showResetButton,
        enableRecaptcha,
        sendToThirdPartyApi,
        sendToApiType,
        customApiEndpoint,
        endpointRequiresAuth,
        endpointUserId,
        endpointPassword,
        displayCustomApiResponse,
    } = fields;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState("");
    const [validators] = useState<DynamicFormValidator[]>([]);
    const [formIndex, setFormIndex] = useState(-1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [customApiResponse, setCustomApiResponse] = useState("");
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();

    useEffect(() => {
        initializeDynamicForm();
    }, []);

    const initializeDynamicForm = () => {
        const indexObj = determineFormIndex(formName, dynamicFormData);
        setFormIndex(indexObj.formIndex);
        if (indexObj.formIndex < 0) {
            AddFormToState({
                formName,
            });
        }
    };

    const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        beginSubmitDynamicForm({ formName });
        setIsSubmitting(true);

        const widgetValidators: DynamicFormValidator[] = [];
        validators.forEach(validator => {
            if (validator) {
                widgetValidators.push(validator);
            }
        });
        const results = widgetValidators.map(v => v.validate());
        if (!results.every(o => o)) {
            setIsSubmitting(false);
            return;
        }

        if (enableRecaptcha) {
            const isReCaptchaValid = await validateReCaptcha({ location: "ContactUs", recaptchaRef });
            if (!isReCaptchaValid) {
                setIsSubmitting(false);
                endSubmitDynamicForm({ formName });
                return;
            }
        }

        const formDataIsValid = isFormValid();

        if (!formDataIsValid) {
            setIsSubmitting(false);
            endSubmitDynamicForm({ formName });
            return;
        }

        const formFields = getCurrentFormFields();

        const data = {
            formName,
            emailRecipients,
            sendToThirdPartyApi,
            sendToApiType,
            customApiEndpoint,
            endpointRequiresAuth,
            endpointUserId,
            endpointPassword,
            fields: formFields,
        };

        submitDynamicForm({
            data,
            onSuccess: (result: any) => {
                setIsSubmitting(false);
                endSubmitDynamicForm({ formName });

                clearDynamicForm({ formName });

                // show api response below form. used by MidlandIndustries for debugging.
                if (displayCustomApiResponse) {
                    setCustomApiResponse(result.submitToApiResponse);
                }

                if (onSuccessfulSubmit !== undefined) {
                    onSuccessfulSubmit();
                }

                if (!skipModalOnSuccess) {
                    setModalMessage(successMessage);
                    setIsModalOpen(true);
                }
            },
            onError: (error: any) => {
                setIsSubmitting(false);
                setModalMessage(error);
                setIsModalOpen(true);
                endSubmitDynamicForm({ formName });
            },
        });
    };

    const isFormValid = () => {
        const formIndex = dynamicFormData?.forms?.findIndex(f => f.name === formName);
        const currentFields = dynamicFormData?.forms[formIndex]?.fields;

        const errorFields = currentFields.filter(f => f.hasError);

        const isValid = currentFields.filter(f => f.hasError).length === 0;
        return isValid;
    };

    const getCurrentFormFields = () => {
        const formIndex = dynamicFormData?.forms?.findIndex(f => f.name === formName);
        const dynamicFormFields = [] as DynamicFormFieldModel[];

        dynamicFormData?.forms[formIndex]?.fields?.forEach(currentField => {
            // We want to target DynamicFormCheckbox elements that are in a group
            if (currentField.type === "DynamicFormCheckbox" && currentField.group !== "") {
                // Find the group object in the dynamicFormData
                const groupIndex = dynamicFormData?.forms[formIndex]?.fields?.findIndex(
                    f => f.key === currentField.group,
                );
                const group = dynamicFormData?.forms[formIndex]?.fields[groupIndex];

                // Add if the rules are satisfied
                if (group.sendOnlyIfChecked === true && currentField.value === true) {
                    dynamicFormFields.push(currentField);
                } else if (group.sendOnlyIfChecked === false) {
                    dynamicFormFields.push(currentField);
                } else {
                    // do not add the field
                }
            } else {
                dynamicFormFields.push(currentField);
            }
        });

        return dynamicFormFields;
    };

    const modalCloseHandler = () => {
        setIsModalOpen(false);
    };

    const continueButtonClickHandler = () => {
        setIsModalOpen(false);
    };

    const getIsSubmitting = () => {
        const formIndex = dynamicFormData?.forms?.findIndex(f => f.name === formName);
        if (formIndex > -1) {
            return dynamicFormData?.forms[formIndex]?.isSubmitting;
        }
    };

    const formStyle = {
        width: "100%",
    };

    const resetForm = () => {
        clearDynamicForm({ formName });
    };

    const generateCustomApiResponseContent = () => {
        let tempDynamicContent = <></>;

        if (customApiResponse && customApiResponse.length > 0) {
            const newLineParts = customApiResponse.split("\n");
            if (newLineParts.length > 0) {
                tempDynamicContent = (
                    <StyledWrapper {...styles.customApiResultWrapper}>
                        {newLineParts.map((newLinePart, index) => (
                            <StyledWrapper key={index} {...styles.customApiResultLineWrapper}>
                                <Typography {...styles.customApiResultText}>{newLinePart}</Typography>
                            </StyledWrapper>
                        ))}
                    </StyledWrapper>
                );
            }
        }

        return tempDynamicContent;
    };

    let customApiResponseContent = <></>;
    if (displayCustomApiResponse) {
        customApiResponseContent = generateCustomApiResponseContent();
    }

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.formGridItem}>
                <LoadingOverlay loading={isSubmitting} {...styles.spinner}>
                    <DynamicFormContext.Provider value={{ formName, validators }}>
                        <form id="dynamicForm" onSubmit={handleFormSubmit} noValidate style={formStyle}>
                            <Zone contentId={id} zoneName="Content" />
                            {children}
                            {enableRecaptcha && <ReCaptcha recaptchaRef={recaptchaRef} location="ContactUs" />}
                            <StyledWrapper {...styles.buttonWrapper}>
                                {showResetButton && (
                                    <Button
                                        {...styles.resetButton}
                                        variant={buttonVariant}
                                        shape={buttonShape}
                                        buttonType={buttonType}
                                        type="reset"
                                        onClick={resetForm}
                                        disabled={getIsSubmitting()}
                                    >
                                        Reset
                                    </Button>
                                )}
                                <Button
                                    {...styles.submitButton}
                                    variant={buttonVariant}
                                    shape={buttonShape}
                                    buttonType={buttonType}
                                    type="submit"
                                    disabled={getIsSubmitting()}
                                >
                                    {submitButtonText}
                                </Button>
                            </StyledWrapper>
                            <Modal
                                headline={translate("Message Sent")}
                                contentLabel={modalMessage}
                                {...styles.successModal}
                                isOpen={isModalOpen}
                                handleClose={modalCloseHandler}
                            >
                                <GridContainer {...styles.modalContainer}>
                                    <GridItem {...styles.messageTextGridItem}>
                                        <Typography {...styles.messageText}>{modalMessage}</Typography>
                                    </GridItem>
                                    <GridItem {...styles.buttonGridItem}>
                                        <Button {...styles.continueButton} onClick={continueButtonClickHandler}>
                                            {translate("Continue")}
                                        </Button>
                                    </GridItem>
                                </GridContainer>
                            </Modal>
                        </form>
                        {customApiResponseContent}
                    </DynamicFormContext.Provider>
                </LoadingOverlay>
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(DynamicForm);
